<template>
    <v-dialog
        v-model="show"
        @click:outside="$emit('update:show', false)"
        max-width="450"
    >
        <v-card v-if="order && step === 1">
            <v-toolbar color="primary" dark flat>
                <v-toolbar-title class="text-capitalize subtitle-1">
                  {{ errorProcessingCard ? 'Oops!' : 'Pay With Bank Transfer' }}
                </v-toolbar-title>

                <v-spacer />
                <v-btn
                  icon
                  color="white"
                  @click="$emit('update:show', false)"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text
                v-if="errorProcessingCard"
                class="pt-6"
            >
                <v-alert
                    dense
                    outlined
                    type="error"
                >
                    There was an error processing your payment with your card
                </v-alert>
                <p class="mb-0">
                    There was an error processing your card for payment.
                    You can alternatively pay with bank transfer.
                    If you choose this option, you will be directed to make payment
                    of {{ formatAmount(orderAmount) }} {{ order.poster_currency }} via Mono
                </p>
            </v-card-text>
            <v-card-text
                v-else
                class="pt-6 text-center"
            >
                <p class="mb-0 subtitle-1">
                    You will now be directed to make payment of
                    <strong>{{ formatAmount(orderAmount) }} {{ order.poster_currency }}</strong>
                    via Mono
                </p>
            </v-card-text>

            <v-card-actions
                v-if="errorProcessingCard"
                class="text-center pb-6"
            >
                <v-btn
                    class="px-3"
                    text
                    color="red darken-2"
                    large
                    @click="cancel"
                >
                    Try Again with Card
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    class="px-3"
                    color="primary"
                    dark
                    depressed
                    large
                    @click="payWithMono"
                >
                    Pay with MONO
                </v-btn>
            </v-card-actions>
            <v-card-actions
                v-else
                class="justify-center pb-6"
            >
                <v-btn
                    class="px-3 text-none"
                    color="primary"
                    dark
                    depressed
                    large
                    @click="payWithMono"
                >
                    Pay with MONO
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-card v-if="step === 2">
            <v-toolbar color="primary" dark flat>
                <v-toolbar-title class="text-capitalize">
                  Oops! Error Making Payment
                </v-toolbar-title>
            </v-toolbar>

            <v-card-text class="pt-6">
                <p class="mb-0 subtitle-1">
                    There was an error making payment via Mono.
                    Please reattempt payment or you can manually make a bank
                    transfer for this payment. Thank you.
                </p>
            </v-card-text>

            <v-card-actions
                class="d-flex flex-wrap justify-center pb-6"
            >
                <v-btn
                    class="px-3"
                    color="primary"
                    dark
                    depressed
                    large
                    @click="payWithMono"
                >
                    Reattempt Payment with Mono
                </v-btn>
                <v-btn
                    class="px-3 mx-0 mt-6"
                    color="primary"
                    dark
                    depressed
                    large
                    @click="step = 3"
                >
                    Pay by Manual Bank Transfer
                </v-btn>
            </v-card-actions>
        </v-card>

        <manual-payment-dialog
            v-if="step === 3"
            :order="order"
            :error="errorProcessingCard"
            @cancel="cancel"
            @verify="verifyPayment"
        />
    </v-dialog>
</template>

<script>
import MonoService from '../../../services/MonoService';
import ManualPaymentDialog from './manual-payment-dialog.vue';

export default {
    props: {
        show: Boolean,
        order: Object,
        errorProcessingCard: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        ManualPaymentDialog,
    },
    data() {
        return {
            step: 1,
        };
    },
    watch: {
        show: function() {
            this.step = 1;
        },
    },
    methods: {
        async payWithMono() {
            const vm = this;
            try {
                let connect;

                const { data } = await Vue.backendApi().order(this.order.id).initiateMonoPayment();
                const countryCode = this.order.poster_country;

                connect = await MonoService.getConnect({
                    scope: 'payments',
                    data: {
                        payment_id: data.id,
                    },
                    onSuccess: () => {
                        vm.verifyPayment();
                    },
                    onClose: () => {
                        vm.step = 2;
                    },
                }, countryCode);

                connect.setup();
                connect.open();
            } catch (err) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading Mono script',
                    text: err,
                });
            }
        },
        async verifyPayment(skipMono = false) {
            const { id } = this.order;
            try {
                this.$emit('loading');
                const { data } = await Vue.backendApi().order(id).verifyPosterPayment({
                    skipMono,
                });
                this.$emit('success', data.status);
            } catch (err) {
                let html = err;
                if (err === 'Payment Not Received Yet') {
                    html += `<div style="text-align: left; padding: 1em;">
                        <br>
                        <ul>
                            <li>While you may have been charged for this transfer by your bank, due to the nature of the internet, payment may be subject to interruptions or delayed transmissions.</li>
                            <li>Kindly retry payment verification on the transfer status page at a later time or contact support for guidance.</li>
                        </ul>
                    </div>`;
                }
                this.$swal({
                    type: 'error',
                    title: 'Error Verifying Payment',
                    html,
                });
            } finally {
                this.$emit('loaded');
            }
        },
        cancel() {
            this.$emit('cancel');
            this.$emit('loaded');
        },
    },
    computed: {
        orderAmount() {
            return (this.order.poster_amount + this.order.poster_fee).toFixed(2);
        },
    },
};
</script>
